import { Fragment, useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import AlertBanner from "../../DesignSystem/AlertBanner/AlertBanner";
import PrimaryButton from "../../DesignSystem/Buttons/PrimaryButton";
import IconButton from "../../DesignSystem/Buttons/IconButton";
import OutlinedStatusButton from "../../DesignSystem/Buttons/OutlinedStatusButtons";
import FileUpload from "../../DesignSystem/Upload/FileUpload";
import UploadedFileList from "../../DesignSystem/Upload/UploadedFileList";
import { useUserEmail } from "../../SignIn/UserEmailContext";
import { ReactComponent as ArrowDownIcon } from "../../../assets/icons/arrow-down.svg";
import { ReactComponent as DownloadIcon } from "../../../assets/icons/download.svg";
import { ReactComponent as UploadIcon } from "../../../assets/icons/upload-icon.svg";
import {
  downloadCommissionSplitTemplate,
  parseCommissionSplitWorkbook,
} from "./commissionSplitBulkWorkbook";
import {
  createCommissionSplitBulkUpload,
  subscribeCommissionSplitBulkRows,
  subscribeCommissionSplitBulkUploads,
} from "./commissionSplitBulkUploadService";
import {
  fetchPolicyIobMatches,
  resolvePolicyReferences,
} from "./commissionSplitPolicyLookupService";
import "../../../css/Finance/CommissionSplits/CommissionSplitBulkUpload.css";

const STATUS_COLOURS = {
  queued: "yellow",
  processing: "blue",
  created: "green",
  completed: "green",
  completed_with_errors: "yellow",
  failed: "red",
  skipped: "yellow",
  skipped_existing: "yellow",
  invalid: "red",
  ready: "blue",
};

const formatStatus = (value) => {
  if (/^skipped[_-]existing$/i.test(String(value || ""))) return "Skipped";
  const text = String(value || "queued").replace(/[_-]+/g, " ");
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const getStatusColour = (value) =>
  STATUS_COLOURS[String(value || "queued").toLowerCase()] || "grey";

const formatDate = (value) => {
  const rawValue = typeof value?.toDate === "function" ? value.toDate() : value;
  const parsedValue = dayjs(rawValue);
  return parsedValue.isValid() ? parsedValue.format("DD MMM YYYY, HH:mm") : "-";
};

const formatUploaderName = (email) => {
  const value = String(email || "").trim();
  if (!value.includes("@")) return value || "-";
  return value.split("@")[0]
    .split(/[._+-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ") || value;
};

const UploadIobRows = ({ rows, loading, fileName }) => (
    <table className="commission-split-striped-table" aria-label={`IOB row status for ${fileName || "upload"}`}>
      <thead>
        <tr>
          <th className="FP_Help_Text_Semibold uppercase white-text">IOB details</th>
          <th className="FP_Help_Text_Semibold uppercase white-text">Split outcome</th>
          <th className="FP_Help_Text_Semibold uppercase white-text">Status</th>
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr>
            <td colSpan="3" className="FP_Help_Text primary">Loading IOB results...</td>
          </tr>
        ) : rows.length ? (
          rows.map((row) => (
            <tr key={row.id}>
              <td>
                <div className="row gap-s">
                  <span className="FP_Help_Text primary-text">{row.rowNumber}.</span>
                  <div className="fit-column gap-s">
                    {row.policyNumber && (
                      <div className="row gap-s">
                        <span className="FP_Help_Text primary">Policy Number</span>
                        <strong className="FP_Help_Text_Semibold">{row.policyNumber}</strong>
                      </div>
                    )}
                    {row.iobNumber && (
                      <div className="row gap-s">
                        <span className="FP_Help_Text primary">IOB Ref</span>
                        <strong className="FP_Help_Text_Semibold">{row.iobNumber}</strong>
                      </div>
                    )}
                  </div>
                </div>
              </td>
              <td>
                <div className="fit-column gap-s">
                  {row.splitId && (
                    <div className="row gap-s">
                      <span className="FP_Help_Text primary">Split ID</span>
                      <strong className="FP_Help_Text_Semibold">{row.splitId}</strong>
                    </div>
                  )}
                  {row.reason && (
                    <div className="row gap-s">
                      <span className="FP_Help_Text primary">Reason</span>
                      <strong className={`FP_Help_Text_Semibold ${["failed", "invalid"].includes(row.status) ? "text-error" : ""}`}>
                        {row.reason}
                      </strong>
                    </div>
                  )}
                  {!row.splitId && !row.reason && <span className="FP_Help_Text primary">-</span>}
                </div>
              </td>
              <td>
                <OutlinedStatusButton
                  label={formatStatus(row.status)}
                  color={getStatusColour(row.status)}
                />
              </td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan="3" className="FP_Help_Text primary">No IOB results found for this upload.</td>
          </tr>
        )}
      </tbody>
    </table>
);

export default function CommissionSplitBulkUpload({ canUpload }) {
  const { userEmail } = useUserEmail();
  const choiceMenuRef = useRef(null);
  const [openChoiceMenu, setOpenChoiceMenu] = useState("");
  const [uploadKind, setUploadKind] = useState("iob");
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [uploads, setUploads] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [expandedUploadId, setExpandedUploadId] = useState(null);
  const [rowState, setRowState] = useState({ uploadId: "", rows: [], loading: false });

  useEffect(() => {
    if (!successMessage) return undefined;
    const timeout = window.setTimeout(() => setSuccessMessage(""), 5000);
    return () => window.clearTimeout(timeout);
  }, [successMessage]);

  useEffect(() => {
    if (!openChoiceMenu) return undefined;
    const closeOutside = (event) => {
      if (!choiceMenuRef.current?.contains(event.target)) setOpenChoiceMenu("");
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOpenChoiceMenu("");
    };
    document.addEventListener("mousedown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [openChoiceMenu]);

  useEffect(
    () =>
      subscribeCommissionSplitBulkUploads(
        (items) => {
          setUploads(items);
          setExpandedUploadId((current) => current === null ? items[0]?.id || "" : current);
          setHistoryLoading(false);
        },
        () => {
          setError("Bulk upload history could not be loaded.");
          setHistoryLoading(false);
        },
      ),
    [],
  );

  useEffect(() => {
    if (!expandedUploadId) {
      setRowState({ uploadId: "", rows: [], loading: false });
      return undefined;
    }
    let active = true;
    setRowState({ uploadId: expandedUploadId, rows: [], loading: true });
    const unsubscribe = subscribeCommissionSplitBulkRows(
      expandedUploadId,
      (rows) => {
        if (active) setRowState({ uploadId: expandedUploadId, rows, loading: false });
      },
      () => {
        if (!active) return;
        setError("The selected upload rows could not be loaded.");
        setRowState({ uploadId: expandedUploadId, rows: [], loading: false });
      },
    );
    return () => {
      active = false;
      unsubscribe();
    };
  }, [expandedUploadId]);

  const handleFileChange = (nextFile) => {
    setFile(nextFile || null);
    setError("");
    setSuccessMessage("");
  };

  const closeUpload = () => {
    if (submitting) return;
    setIsUploadOpen(false);
    handleFileChange(null);
  };

  const handleAddSplits = async () => {
    if (!file) return;
    try {
      setSubmitting(true);
      setError("");
      setSuccessMessage("");
      let parsedUpload = parseCommissionSplitWorkbook(
        await file.arrayBuffer(),
        file.name,
        uploadKind,
      );
      if (uploadKind === "policy" && parsedUpload.validRows.length) {
        const matches = await fetchPolicyIobMatches(
          parsedUpload.validRows.map((row) => row.policyNumber),
          userEmail,
        );
        parsedUpload = resolvePolicyReferences(parsedUpload, matches);
      }
      const uploadId = await createCommissionSplitBulkUpload(parsedUpload, userEmail);
      setExpandedUploadId(uploadId);
      setSuccessMessage(
        [
          parsedUpload.validCount
            ? `${parsedUpload.validCount} IOB reference${parsedUpload.validCount === 1 ? "" : "s"} queued for processing.`
            : "No resolved IOB references were queued.",
          parsedUpload.invalidCount
            ? `${parsedUpload.invalidCount} invalid or duplicate reference${parsedUpload.invalidCount === 1 ? " was" : "s were"} skipped.`
            : "",
          "Statuses will update live below.",
        ].filter(Boolean).join(" "),
      );
      setFile(null);
      setIsUploadOpen(false);
    } catch (submitError) {
      setError(submitError.message || "The references could not be added.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!canUpload) {
    return (
      <div className="commission-bulk-scroll column gap-s">
        <AlertBanner
          type="error"
          message="You need Finance, Finance Admin or Administrator permission to bulk upload references."
          showClose={false}
        />
      </div>
    );
  }

  return (
    <div className="commission-bulk-scroll column gap-l">
      {error && (
        <AlertBanner
          type="error"
          message={error}
          onClose={() => setError("")}
        />
      )}
      {successMessage && (
        <AlertBanner
          type="success"
          message={successMessage}
          onClose={() => setSuccessMessage("")}
        />
      )}

      <header ref={choiceMenuRef} className="commission-bulk-header row justify-flex-end align-center gap-s">
        <div className="commission-bulk-choice">
          <IconButton
            icon={<DownloadIcon aria-hidden="true" />}
            label="Download template"
            onClick={() => setOpenChoiceMenu((current) => current === "download" ? "" : "download")}
          />
          {openChoiceMenu === "download" && (
            <div className="commission-bulk-choice-menu">
              <div className="commission-bulk-choice-menu-header">
                <strong>Download Excel</strong>
                <span>Select a reference type</span>
              </div>
              <button type="button" onClick={() => { downloadCommissionSplitTemplate("iob"); setOpenChoiceMenu(""); }}>IOB references</button>
              <button type="button" onClick={() => { downloadCommissionSplitTemplate("policy"); setOpenChoiceMenu(""); }}>Policy numbers</button>
            </div>
          )}
        </div>
        <div className="commission-bulk-choice">
          <IconButton
            icon={<UploadIcon aria-hidden="true" />}
            label="Upload references"
            onClick={() => setOpenChoiceMenu((current) => current === "upload" ? "" : "upload")}
          />
          {openChoiceMenu === "upload" && (
            <div className="commission-bulk-choice-menu">
              <div className="commission-bulk-choice-menu-header">
                <strong>Upload Excel</strong>
                <span>Select a reference type</span>
              </div>
              <button type="button" onClick={() => { setUploadKind("iob"); setOpenChoiceMenu(""); setIsUploadOpen(true); }}>IOB references</button>
              <button type="button" onClick={() => { setUploadKind("policy"); setOpenChoiceMenu(""); setIsUploadOpen(true); }}>Policy numbers</button>
            </div>
          )}
        </div>
      </header>

      {isUploadOpen && (
        <div
          className="commission-bulk-upload-overlay"
          role="presentation"
          onMouseDown={(event) =>
            event.target === event.currentTarget && closeUpload()
          }
        >
          <div
            className="commission-bulk-upload-modal column gap-l"
            role="dialog"
            aria-modal="true"
            aria-labelledby="commission-bulk-upload-title"
          >
            <div className="row space-between align-center gap-m">
              <div className="fit-column gap-m">
                <h3 id="commission-bulk-upload-title" className="FP_Content_Semibold">
                  Upload {uploadKind === "policy" ? "policy numbers" : "IOB references"}
                </h3>
                <p className="FP_Help_Text align-left">
                  {uploadKind === "policy"
                    ? "Select an Excel file containing only the Policy Number column. IOB references will be looked up in Snowflake before splits are queued."
                    : "Select an Excel file containing only the IOB Ref column. The required split information will be retrieved automatically."}
                </p>
              </div>
              <button
                type="button"
                className="commission-bulk-upload-close"
                onClick={closeUpload}
                aria-label="Close upload dialog"
              >
                ×
              </button>
            </div>
            <FileUpload
              label={`Excel file containing ${uploadKind === "policy" ? "policy numbers" : "IOB refs"}`}
              description="Drag and drop the completed one-column template here or"
              browseText="Browse files"
              accept=".xlsx,.xls"
              fileName={file?.name || ""}
              files={file ? [file] : []}
              onFilesChange={handleFileChange}
              disabled={submitting}
              status={error ? "error" : "default"}
              statusMessage={error}
            />
            {file && (
              <UploadedFileList
                files={[file]}
                onRemove={() => handleFileChange(null)}
              />
            )}
            <div className="row justify-flex-end gap-m">
              <PrimaryButton
                type="button"
                label={submitting ? "ADDING SPLITS..." : "ADD SPLITS"}
                onClick={handleAddSplits}
                disabled={!file || submitting}
              />
            </div>
          </div>
        </div>
      )}

      <section className="fit-column gap-s">
        <div className="table-scroll-container">
          <table>
            <thead>
              <tr>
                {[
                  "Upload",
                  "Details",
                  "Status",
                ].map((heading) => (
                  <th key={heading} className="FP_Help_Text_Semibold uppercase white-text">
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {historyLoading ? (
                <tr>
                  <td colSpan="3" className="FP_Help_Text primary">Loading upload history...</td>
                </tr>
              ) : uploads.length ? (
                uploads.map((upload, index) => {
                  const isExpanded = expandedUploadId === upload.id;
                  const rowsLoading = rowState.uploadId !== upload.id || rowState.loading;
                  return (
                  <Fragment key={upload.id}>
                  <tr
                    className={`commission-bulk-history-row commission-bulk-history-row--${index % 2 === 0 ? "blue100" : "blue200"}`}
                    onClick={() => {
                      setExpandedUploadId((current) => current === upload.id ? "" : upload.id);
                    }}
                  >
                    <td>
                      <div className="row gap-s">
                        <span className="FP_Help_Text primary-text">{index + 1}.</span>
                        <div className="fit-column gap-s">
                          <div className="row gap-s">
                            <span className="FP_Help_Text primary">Uploaded</span>
                            <strong className="FP_Help_Text_Semibold">{formatDate(upload.createdAt)}</strong>
                          </div>
                          <div className="row gap-s">
                            <span className="FP_Help_Text primary">File</span>
                            <strong className="FP_Help_Text_Semibold">{upload.fileName || "-"}</strong>
                          </div>
                          <div className="row gap-s">
                            <span className="FP_Help_Text primary">Uploaded by</span>
                            <strong className="FP_Help_Text_Semibold" title={upload.createdBy || undefined}>
                              {formatUploaderName(upload.createdBy)}
                            </strong>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="commission-bulk-summary fit-column gap-s">
                        {[
                          ["Total", upload.summary?.totalRows],
                          ["Processed", upload.summary?.processed],
                          ["Successful", upload.summary?.successful],
                          ["Completed", upload.summary?.completed],
                          ["Failed", upload.summary?.failed],
                          ["Skipped / Invalid", upload.summary?.skippedInvalid],
                        ].map(([label, value]) => (
                          <div className="commission-bulk-summary-line" key={label}>
                            <span className="FP_Help_Text primary">{label}</span>
                            <strong className="FP_Help_Text_Semibold">{Number(value || 0)}</strong>
                          </div>
                        ))}
                      </div>
                    </td>
                    <td>
                      <div className="row align-center justify-space-between gap-s">
                        <OutlinedStatusButton
                          label={formatStatus(upload.status)}
                          color={getStatusColour(upload.status)}
                        />
                        <button
                          type="button"
                          className="commission-bulk-expand-toggle"
                          aria-label={`${isExpanded ? "Collapse" : "Expand"} IOB details for ${upload.fileName || "upload"}`}
                          aria-expanded={isExpanded}
                          aria-controls={isExpanded ? `commission-bulk-rows-${upload.id}` : undefined}
                          onClick={(event) => {
                            event.stopPropagation();
                            setExpandedUploadId((current) => current === upload.id ? "" : upload.id);
                          }}
                        >
                          <ArrowDownIcon aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="commission-bulk-expanded-row">
                      <td colSpan="3">
                        <div id={`commission-bulk-rows-${upload.id}`}>
                          <UploadIobRows
                            rows={rowState.uploadId === upload.id ? rowState.rows : []}
                            loading={rowsLoading}
                            fileName={upload.fileName}
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="3" className="FP_Help_Text primary">No bulk uploads found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

    </div>
  );
}
